import { useRef, useState } from 'react';
import { BoardEffectTile, isBoardEffectTile } from './BoardEffectIcons';
import TreasureChestIcon from './TreasureChestIcon';
import { getBoardSquareColor, getBoardSquareLabel } from './doorBoard';
import { BOUNTY_TAG_REWARD, getBountyTagState } from './bountyTag';
import { playSound } from '../../utils/utils';
import './BountyTag.css';

const BountyTagControl = ({ game, onMark, onClear, fullScreen = false }) => {
  const [selecting, setSelecting] = useState(fullScreen);
  const pickerButton = useRef(null);
  const tag = getBountyTagState(game);
  if (!tag.owned || !tag.canMark) return null;
  return (
    <section
      className={`coreboundBountySetup ${tag.armed ? 'is-armed' : ''}`}
      aria-label="Bounty Tag setup"
    >
      <div className="coreboundBountyHeader">
        <div>
          <strong>
            ⌖ Bounty Tag <small>SPECIAL · ACTIVE</small>
          </strong>
          <span role="status">
            {tag.armed
              ? `Marked · Row ${Math.floor(tag.targetIndex / 5) + 1}, column ${
                  (tag.targetIndex % 5) + 1
                }`
              : `Mark a tile · Land there and clear the room for +${BOUNTY_TAG_REWARD} shards`}
          </span>
          <span>The mark is spent on this throw, even if you miss.</span>
        </div>
        {!fullScreen && (
          <button
            ref={pickerButton}
            type="button"
            aria-expanded={selecting}
            onClick={() => setSelecting((open) => !open)}
          >
            {selecting
              ? 'Close picker'
              : tag.armed
              ? 'Change bounty tile'
              : 'Mark bounty tile'}
          </button>
        )}
      </div>
      {selecting && (
        <div className="coreboundBountyPicker">
          <p>
            <b>1. Tap a tile below.</b> Then aim your die at it and defeat the
            enemy on that throw to earn +{BOUNTY_TAG_REWARD} shards. Missing
            spends the mark.
          </p>
          <div
            className="coreboundBountyGrid"
            role="group"
            aria-label="Choose bounty tile"
          >
            {game.doorBoard.map((square, index) => (
              <button
                key={index}
                type="button"
                className={index === tag.targetIndex ? 'is-marked' : ''}
                style={{ '--tile-color': getBoardSquareColor(square) }}
                aria-pressed={index === tag.targetIndex}
                aria-label={`Row ${Math.floor(index / 5) + 1}, column ${
                  (index % 5) + 1
                }: ${getBoardSquareLabel(square)}`}
                onClick={() => {
                  if (index !== tag.targetIndex)
                    playSound('/sounds/menu-glitch-select-option.mp3');
                  onMark(index);
                  if (!fullScreen) setSelecting(false);
                  pickerButton.current?.focus({ preventScroll: true });
                }}
              >
                <small>
                  {index === tag.targetIndex ? 'TAGGED' : index + 1}
                </small>
                {isBoardEffectTile(square) ? (
                  <BoardEffectTile effect={square.effect} />
                ) : square.effect?.type === 'relic' ? (
                  <TreasureChestIcon />
                ) : (
                  <span>
                    {square.effect?.type === 'critical'
                      ? '×2'
                      : square.effect?.type === 'criticalMiss'
                      ? '÷3'
                      : getBoardSquareLabel(square)}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
      {tag.armed && (
        <div className="coreboundBountyFooter">
          <span>
            +{BOUNTY_TAG_REWARD} shards on a successful marked landing · Rooms
            only
          </span>
          <button
            type="button"
            onClick={() => {
              playSound('/sounds/menu-glitch-select-option.mp3');
              onClear();
              setSelecting(false);
            }}
          >
            Clear bounty tag
          </button>
        </div>
      )}
    </section>
  );
};

export default BountyTagControl;
